// Offline sales queue for the till. When the connection is down a sale is
// kept here (localStorage, per shop) with offlineAt = when it was made, and
// sent with checkout_sale once the connection is back. checkout_sale is
// idempotent on the sale id, so a resend after a lost reply never charges
// twice. Only sales that need no live balance are queued: no wallet /
// points tenders and no wallet top-ups.
import { checkoutSale } from "../../lib/api.js";

const KEY = (tid) => "petos:pos:outbox:" + tid;
const read = (tid) => { try { const v = JSON.parse(localStorage.getItem(KEY(tid)) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } };
const write = (tid, list) => { try { localStorage.setItem(KEY(tid), JSON.stringify(list)); } catch { /* storage full / private mode */ } };

export const outboxList = (tid) => read(tid);

export function outboxAdd(tid, entry) {
  const list = read(tid).filter(e => e.saleId !== entry.saleId);
  list.push({ ...entry, queuedAt: new Date().toISOString(), tries: 0, error: null });
  write(tid, list);
  return list;
}

export function outboxRemove(tid, saleId) {
  const list = read(tid).filter(e => e.saleId !== saleId);
  write(tid, list);
  return list;
}

// A network failure (not a refusal from the server): keep the sale queued.
export const isNetworkError = (e) => /net-timeout|Failed to fetch|NetworkError|Load failed|timeout|network|ERR_INTERNET|offline/i.test(String(e?.message || e || ""));

// Sales that can wait for the connection.
export const canQueue = (payload) =>
  !(payload.lines || []).some(l => l.kind === "wallet_topup") &&
  !(payload.payments || []).some(p => p.method === "wallet" || p.method === "points");

let syncing = null;
// Sends queued sales oldest first. Stops at the first network failure (the
// connection is still down); a refusal is kept with its error for the
// manager to retry or discard. Returns { sent, list }.
export function outboxSync(tid) {
  if (syncing) return syncing;
  syncing = (async () => {
    let sent = 0;
    for (const e of read(tid)) {
      if (e.error && !e.retry) continue;
      try {
        await checkoutSale(e.payload);
        outboxRemove(tid, e.saleId); sent++;
      } catch (err) {
        if (isNetworkError(err)) break;
        write(tid, read(tid).map(x => x.saleId === e.saleId ? { ...x, tries: (x.tries || 0) + 1, error: String(err?.message || err), retry: false } : x));
      }
    }
    return { sent, list: read(tid) };
  })().finally(() => { syncing = null; });
  return syncing;
}

// Mark a refused sale to be tried again on the next sync.
export function outboxRetry(tid, saleId) {
  write(tid, read(tid).map(x => x.saleId === saleId ? { ...x, retry: true, error: null } : x));
  return read(tid);
}
